/**
 * Retail Evotor cloud-POS API client (BFF: `/api/v1/store/settings/retail-evotor`).
 *
 * A thin, tenant-aware passthrough facade over `evotor-integration-service` (see
 * `web-merchant-portal-bff` user-story-031) — 26 operations across 10 OpenAPI tags. Not a
 * `VersionedSettings` domain: accounts/stores are paginated CRUD/toggle lists, and every
 * export/import operation is a stateless trigger.
 *
 * Store binding/stock-export (`bindRetailEvotorStoreLocation`, `unbindRetailEvotorStoreLocation`,
 * `setRetailEvotorStoreStockExport` — plan-integration-010 Phase 6) are the exception to the
 * "stateless trigger" rule above: they are resource-oriented CRUD on the store's own binding/
 * switch state, so they carry a typed JSON request body and (assumed) JSON response, like the
 * account create/update calls, not the `text/plain` triggers.
 *
 * **Load-bearing transport notes (see `apiClient.test.ts` for the empirical regression proof):**
 *  - Every toggle/trigger endpoint responds `text/plain` (a human-readable upstream message, e.g.
 *    "Началась выгрузка продуктов в Эвотор") — never JSON. `POST /stores/import` responds `200`
 *    with **no body at all**. `apiClient`'s instance-level `Accept: 'application/json'` default is
 *    tuned for the rest of this app, so every such call here overrides `Accept` to a wildcard —
 *    otherwise Spring's content negotiation 406s a request whose `Accept` doesn't include the
 *    endpoint's only `produces` type (the same class of bug already documented in
 *    `catalogImportApi.ts`'s `/template` download). No `responseType` override is used: axios's
 *    default `transformResponse` already returns a non-JSON success body unparsed (a silent
 *    `JSON.parse` failure falls through to the raw string) while still parsing a genuine JSON
 *    *error* body normally — forcing `responseType: 'text'` would flatten JSON error bodies too
 *    and degrade `normalizeApiError`.
 *  - The account/store sync toggle sends `active` as an HTTP **header** (`RetailEvotorActiveHeader`
 *    in the contract), not a query param or request body.
 *  - The account create/update body has **no partial-update semantics** — `token` is always
 *    required, including on edit (no "leave blank to keep the existing secret").
 *  - `sort` is sent as a single string (e.g. `"createdAt:desc"`), matching the contract's example
 *    format, even though the OpenAPI schema types the query param as an array.
 *  - As of adr-integration-012/adr-integration-013 (2026-08-30), every bulk trigger below
 *    (import/export/status-update/delete-all) is strictly **tenant-scoped** — see `settingsTypes.ts`
 *    §21's header note. Per-function comments below no longer claim a cross-tenant blast radius.
 */
import { apiClient } from '@/shared/api';
import type {
  BindRetailEvotorStoreLocationRequest,
  RetailEvotorAccount,
  RetailEvotorAccountCreateRequest,
  RetailEvotorAccountUpdateRequest,
  RetailEvotorAccountsQuery,
  RetailEvotorDevice,
  RetailEvotorDevicesQuery,
  RetailEvotorDocumentsImportParams,
  RetailEvotorEntityDeleteRequest,
  RetailEvotorMasterDataStatus,
  RetailEvotorPage,
  RetailEvotorStore,
  RetailEvotorStoreProductGroupsImportParams,
  RetailEvotorStoreProductsImportParams,
  RetailEvotorStoresQuery,
  SetRetailEvotorStorePriceExportRequest,
  SetRetailEvotorStoreStockExportRequest,
} from '../model/settingsTypes';

const BASE = '/api/v1/store/settings/retail-evotor';

/** Wildcard Accept header for the `text/plain`-only endpoints — see file header. */
const TEXT_HEADERS = { Accept: '*/*' } as const;

/** Unwraps a trigger/toggle response to its `text/plain` payload; `''` for a genuinely empty body. */
async function unwrapText(request: Promise<{ data: unknown }>): Promise<string> {
  const { data } = await request;
  return typeof data === 'string' ? data : '';
}

/** Drops `undefined`/`null`/`''` entries so unset optional filters are omitted, not sent empty. */
function cleanParams<T extends object>(params: T): Partial<T> {
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') {
      continue;
    }
    cleaned[key] = value;
  }
  return cleaned as Partial<T>;
}

/**
 * Generates an `Idempotency-Key` for account creation (cheap double-submit protection; optional
 * per the contract). Mirrors `features/loyalty/lib/idempotency.ts`'s fallback for environments
 * without `crypto.randomUUID`.
 */
export function newRetailEvotorIdempotencyKey(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// ── Accounts (RetailEvotorAccounts tag — every mutation, including the toggle, is credential-gated) ─

export const listRetailEvotorAccounts = async (
  query: RetailEvotorAccountsQuery,
): Promise<RetailEvotorPage<RetailEvotorAccount>> =>
  (
    await apiClient.get<RetailEvotorPage<RetailEvotorAccount>>(`${BASE}/accounts`, {
      params: cleanParams(query),
    })
  ).data;

export const getRetailEvotorAccount = async (evotorId: string): Promise<RetailEvotorAccount> =>
  (await apiClient.get<RetailEvotorAccount>(`${BASE}/accounts/${encodeURIComponent(evotorId)}`))
    .data;

export const createRetailEvotorAccount = async (
  body: RetailEvotorAccountCreateRequest,
  idempotencyKey?: string,
): Promise<RetailEvotorAccount> =>
  (
    await apiClient.post<RetailEvotorAccount>(`${BASE}/accounts`, body, {
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
    })
  ).data;

/** No `evotorId` in the body — it is the immutable path key. */
export const updateRetailEvotorAccount = async (
  evotorId: string,
  body: RetailEvotorAccountUpdateRequest,
): Promise<RetailEvotorAccount> =>
  (
    await apiClient.put<RetailEvotorAccount>(
      `${BASE}/accounts/${encodeURIComponent(evotorId)}`,
      body,
    )
  ).data;

/** Bulk-delete by OBZR UUID (`RetailEvotorAccount.id`), not `evotorId`. Removes the link + token
 * from the platform only — does not delete the account in the Evotor cloud itself. */
export const deleteRetailEvotorAccounts = async (
  body: RetailEvotorEntityDeleteRequest,
): Promise<void> => {
  await apiClient.delete(`${BASE}/accounts`, { data: body });
};

/** `active` is an HTTP header (`RetailEvotorActiveHeader`), not a query param or body field. */
export const toggleRetailEvotorAccount = async (
  evotorId: string,
  active: boolean,
): Promise<string> =>
  unwrapText(
    apiClient.patch(`${BASE}/accounts/${encodeURIComponent(evotorId)}/active`, undefined, {
      headers: { ...TEXT_HEADERS, active: String(active) },
    }),
  );

// ── Stores (RetailEvotorStore tag) ────────────────────────────────────────────────────────────────

export const listRetailEvotorStores = async (
  query: RetailEvotorStoresQuery,
): Promise<RetailEvotorPage<RetailEvotorStore>> =>
  (
    await apiClient.get<RetailEvotorPage<RetailEvotorStore>>(`${BASE}/stores`, {
      params: cleanParams(query),
    })
  ).data;

/** `active` is an HTTP header, same mechanics as the account toggle. */
export const toggleRetailEvotorStore = async (evotorId: string, active: boolean): Promise<string> =>
  unwrapText(
    apiClient.patch(`${BASE}/stores/${encodeURIComponent(evotorId)}/active`, undefined, {
      headers: { ...TEXT_HEADERS, active: String(active) },
    }),
  );

/**
 * Imports stores for every one of the tenant's own Evotor accounts with active sync
 * (tenant-scoped, adr-integration-012). Responds `200` with **no body** — resolves to `''`; the
 * caller supplies its own success copy.
 */
export const importRetailEvotorStores = async (): Promise<string> =>
  unwrapText(apiClient.post(`${BASE}/stores/import`, undefined, { headers: TEXT_HEADERS }));

/**
 * Binds an Evotor store to an OBZR fulfilment location (plan-integration-010 Phase 6,
 * user-story-integration-024). Several stores — including from different Evotor accounts — may
 * bind to the **same** location; re-binding an already-bound store replaces the previous binding
 * rather than erroring (AC3).
 *
 * Unlike the `text/plain` triggers above, this is a resource-oriented `PUT` with a typed JSON
 * request body, so the response is modelled as the updated {@link RetailEvotorStore} — consistent
 * with `createRetailEvotorAccount`/`updateRetailEvotorAccount` rather than the toggle/import
 * triggers. The store-list hook invalidates rather than trusting this response shape directly
 * (see `useBindRetailEvotorStoreLocation`), so a contract mismatch here degrades to an extra
 * refetch rather than a broken UI.
 */
export const bindRetailEvotorStoreLocation = async (
  evotorId: string,
  body: BindRetailEvotorStoreLocationRequest,
): Promise<RetailEvotorStore> =>
  (
    await apiClient.put<RetailEvotorStore>(
      `${BASE}/stores/${encodeURIComponent(evotorId)}/binding`,
      body,
    )
  ).data;

/**
 * Unbinds an Evotor store from its location. Stock export for it stops immediately — export
 * fails closed for an unbound store (adr-integration-011).
 */
export const unbindRetailEvotorStoreLocation = async (evotorId: string): Promise<void> => {
  await apiClient.delete(`${BASE}/stores/${encodeURIComponent(evotorId)}/binding`);
};

/**
 * Enables/disables per-store stock export (user-story-integration-025). Unlike the `active` sync
 * toggle above, this newer endpoint takes a JSON body (`{ enabled }`), not a header — confirmed
 * against the BFF contract built in parallel with this UI (plan-integration-010 Phase 6).
 */
export const setRetailEvotorStoreStockExport = async (
  evotorId: string,
  body: SetRetailEvotorStoreStockExportRequest,
): Promise<RetailEvotorStore> =>
  (
    await apiClient.patch<RetailEvotorStore>(
      `${BASE}/stores/${encodeURIComponent(evotorId)}/stock-export`,
      body,
    )
  ).data;

/**
 * Enables/disables per-store PRICE export (adr-integration-017, plan-platform-002 WP3.2) — the
 * `stock-export` twin on the price axis, confirmed live on the shipped BFF contract
 * (2026-09-03). **Gated by `settings.pricing.manage`** (the admin tier), unlike every other write
 * on this tag (which needs only `settings.retail-evotor.manage`) — this operation repoints an
 * entire store's till prices onto the platform's governed price feed, the same class of operation
 * adr-pricing-025 (decision 9) gates at that tier on the price-list store-binding surfaces.
 */
export const setRetailEvotorStorePriceExport = async (
  evotorId: string,
  body: SetRetailEvotorStorePriceExportRequest,
): Promise<RetailEvotorStore> =>
  (
    await apiClient.patch<RetailEvotorStore>(
      `${BASE}/stores/${encodeURIComponent(evotorId)}/price-export`,
      body,
    )
  ).data;

// ── Products (RetailEvotorProduct tag) — master-catalog export ─────────────────────────────────────

/** Exports one product (tenant-scoped). Not surfaced on this settings page (see design spec §5). */
export const exportRetailEvotorProduct = async (productId: string): Promise<string> =>
  unwrapText(
    apiClient.post(`${BASE}/products/export/${encodeURIComponent(productId)}`, undefined, {
      headers: TEXT_HEADERS,
    }),
  );

/** Exports every `READY_FOR_EXPORT` product for the calling tenant (tenant-scoped). */
export const exportAllRetailEvotorProducts = async (): Promise<string> =>
  unwrapText(apiClient.post(`${BASE}/products/export/all`, undefined, { headers: TEXT_HEADERS }));

/** A tenant-scoped technical/maintenance operation. */
export const changeAllRetailEvotorProductStatuses = async (
  status: RetailEvotorMasterDataStatus,
): Promise<string> =>
  unwrapText(
    apiClient.post(`${BASE}/products/status-update/all`, undefined, {
      headers: TEXT_HEADERS,
      params: { status },
    }),
  );

/** A tenant-scoped technical/maintenance operation. */
export const checkRetailEvotorProductsReadyForExport = async (): Promise<string> =>
  unwrapText(
    apiClient.post(`${BASE}/products/status-update/ready-for-export`, undefined, {
      headers: TEXT_HEADERS,
    }),
  );

// ── Product Groups (RetailEvotorProductGroup tag) ───────────────────────────────────────────────────

/** Exports one product group (tenant-scoped). Not surfaced on this settings page (see design spec §5). */
export const exportRetailEvotorProductGroup = async (productGroupId: string): Promise<string> =>
  unwrapText(
    apiClient.post(
      `${BASE}/product-groups/export/${encodeURIComponent(productGroupId)}`,
      undefined,
      { headers: TEXT_HEADERS },
    ),
  );

export const exportAllRetailEvotorProductGroups = async (): Promise<string> =>
  unwrapText(
    apiClient.post(`${BASE}/product-groups/export/all`, undefined, { headers: TEXT_HEADERS }),
  );

// ── Store Products (RetailEvotorStoreProduct tag) — import from Evotor ─────────────────────────────

/** Imports for every one of the tenant's own accounts with active sync (tenant-scoped). */
export const importRetailEvotorStoreProducts = async (
  params: RetailEvotorStoreProductsImportParams,
): Promise<string> =>
  unwrapText(
    apiClient.post(`${BASE}/store-products/import`, undefined, {
      headers: TEXT_HEADERS,
      params: cleanParams(params),
    }),
  );

/** Dev-contour only upstream — returns 403 on stage/prod. Deletes only the calling tenant's own
 * data (tenant-scoped). */
export const deleteAllRetailEvotorStoreProducts = async (): Promise<string> =>
  unwrapText(apiClient.delete(`${BASE}/store-products/delete/all`, { headers: TEXT_HEADERS }));

// ── Store Product Groups (RetailEvotorStoreProductGroup tag) ────────────────────────────────────────

export const importRetailEvotorStoreProductGroups = async (
  params: RetailEvotorStoreProductGroupsImportParams,
): Promise<string> =>
  unwrapText(
    apiClient.post(`${BASE}/store-product-groups/import`, undefined, {
      headers: TEXT_HEADERS,
      params: cleanParams(params),
    }),
  );

/** Dev-contour only upstream — returns 403 on stage/prod. Tenant-scoped (see file header). */
export const deleteAllRetailEvotorStoreProductGroups = async (): Promise<string> =>
  unwrapText(
    apiClient.delete(`${BASE}/store-product-groups/delete/all`, { headers: TEXT_HEADERS }),
  );

// ── Employees / Roles (import from Evotor, tenant-scoped) ──────────────────────────────────────────

export const importRetailEvotorEmployees = async (): Promise<string> =>
  unwrapText(apiClient.post(`${BASE}/employees/import`, undefined, { headers: TEXT_HEADERS }));

export const importRetailEvotorRoles = async (): Promise<string> =>
  unwrapText(apiClient.post(`${BASE}/roles/import`, undefined, { headers: TEXT_HEADERS }));

// ── Devices (RetailEvotorDevice tag) ────────────────────────────────────────────────────────────────

export const importRetailEvotorDevices = async (): Promise<string> =>
  unwrapText(apiClient.post(`${BASE}/devices/import`, undefined, { headers: TEXT_HEADERS }));

/**
 * Lists smart terminals (kassas) linked to the tenant's Evotor accounts, optionally filtered by
 * `evotorId`/`name`/`storeEvotorId`. Unlike the trigger endpoints above, this one responds JSON —
 * same `RetailEvotorPage<T>` envelope as {@link listRetailEvotorAccounts}/{@link listRetailEvotorStores}
 * — so it does **not** need the `TEXT_HEADERS` override.
 */
export const listRetailEvotorDevices = async (
  query: RetailEvotorDevicesQuery,
): Promise<RetailEvotorPage<RetailEvotorDevice>> =>
  (
    await apiClient.get<RetailEvotorPage<RetailEvotorDevice>>(`${BASE}/devices`, {
      params: cleanParams(query),
    })
  ).data;

// ── Store Documents (RetailEvotorStoreDocument tag) ──────────────────────────────────────────────────

export const importRetailEvotorDocuments = async (
  params: RetailEvotorDocumentsImportParams,
): Promise<string> =>
  unwrapText(
    apiClient.post(`${BASE}/documents/import`, undefined, {
      headers: TEXT_HEADERS,
      params: cleanParams(params),
    }),
  );
